import React, { useState, useEffect } from 'react';
import { Users, Award, ShieldCheck, Mail, Sparkles, BookOpen, Search, ChevronRight, CheckCircle2 } from 'lucide-react';
import { StorageService } from '../services/storageService';
import { ModuleHeader } from '../components/ModuleHeader';
import { AdUnit } from '../components/AdUnit';
import { useActiveTheme, useThemeTokens } from '../hooks/useActiveTheme';

interface FacultyMember {
  _id: string;
  name: string;
  title: string;
  credentials: string;
  bio: string;
  avatarUrl?: string;
  specialties: string[];
  order: number;
  isDesignatedAuthor: boolean;
  email?: string;
}

export const Faculty: React.FC = () => {
  const activeTheme = useActiveTheme();
  const tokens = useThemeTokens();
  const isClinicalTheme = activeTheme === 'clinical_trust';
  const [facultyList, setFacultyList] = useState<FacultyMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialty, setSelectedSpecialty] = useState('All');

  useEffect(() => {
    const fetchFaculty = async () => {
      try {
        const data = await StorageService.getFaculty();
        setFacultyList(data);
      } catch (err) {
        console.error("Failed to load school faculty:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchFaculty();
  }, []);

  const allSpecialties = React.useMemo(() => {
    const list = new Set<string>();
    facultyList.forEach(m => {
      if (Array.isArray(m.specialties)) {
        m.specialties.forEach(spec => list.add(spec));
      }
    });
    return ['All', ...Array.from(list)];
  }, [facultyList]);

  const filteredFaculty = React.useMemo(() => {
    return facultyList.filter(m => {
      const matchesSearch = m.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                            m.bio.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            m.title.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesSpecialty = selectedSpecialty === 'All' || 
                               (Array.isArray(m.specialties) && m.specialties.includes(selectedSpecialty));

      return matchesSearch && matchesSpecialty;
    });
  }, [facultyList, searchTerm, selectedSpecialty]);

  return (
    <div className="max-w-6xl mx-auto pb-24 text-slate-900 dark:text-slate-100 animate-fade-in">
      <AdUnit placement="ai_tutor" />

      <ModuleHeader 
        icon={Users}
        title="Our Teachers & Experts"
        description="Meet the experienced teachers and caregivers who write and check our study lessons and practice tests."
        badge="Verified Experts"
        color="indigo"
      />

      {/* Trust Banner */}
      <div id="author-trust-banner" className={`mt-8 mb-10 p-6 md:p-8 rounded-[2.5rem] flex flex-col md:flex-row items-center gap-6 shadow-sm border ${
        tokens.isWarmAcademic
          ? 'bg-[#E6F4F1] text-[#2D3748] dark:text-slate-100 border-[#0D6E6E]/20'
          : isClinicalTheme 
          ? 'bg-[#0B2942] text-white border-slate-800' 
          : 'bg-gradient-to-r from-indigo-50 to-slate-50 dark:from-slate-900 dark:to-slate-950 border-slate-200 dark:border-slate-800'
      }`}>
        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg shrink-0 ${
          tokens.isWarmAcademic
            ? 'bg-[#0D6E6E] text-white'
            : isClinicalTheme ? 'bg-[#0F7D6C] text-white' : 'bg-indigo-600 text-white'
        }`}>
          <Award size={28} />
        </div>
        <div className="space-y-1.5 text-center md:text-left">
          <h4 className={`text-sm tracking-wide uppercase ${
            tokens.isWarmAcademic ? 'font-bold text-[#0D6E6E]' : isClinicalTheme ? 'font-serif font-bold text-amber-300' : 'font-black text-slate-900 dark:text-white'
          }`}>
            The Diamond Educational Standard
          </h4>
          <p className={`text-xs font-medium leading-relaxed max-w-3xl ${
            tokens.isWarmAcademic ? 'text-[#4A5568] dark:text-slate-300' : isClinicalTheme ? 'text-slate-200' : 'text-slate-600 dark:text-slate-400'
          }`}>
            Every study syllabus, math simulation, practice inquiry, and clinical guideline on this platform is scientifically formulated and peer-reviewed by active RN instructors, nurse practitioners, and academic writers with decades of bedside teaching experience.
          </p>
        </div>
      </div>

      {/* Main Grid View */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        {/* Left Sidebar Filter/Search Control Column */}
        <div className="lg:col-span-4 space-y-6">
          <div className="bg-white dark:bg-slate-900 border border-[#E3DFD5] dark:border-slate-800 p-6 rounded-[2.5rem] shadow-sm space-y-6">
            <h3 className="text-xs font-black uppercase tracking-widest text-slate-400">Search Faculty</h3>
            
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input 
                type="text" 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search name or bio..."
                className={`w-full pl-11 pr-4 py-3.5 bg-slate-50 dark:bg-slate-950 border border-slate-150 dark:border-slate-800 rounded-2xl outline-none font-bold text-xs ${
                  tokens.isWarmAcademic ? 'focus:border-[#0D6E6E]' : isClinicalTheme ? 'focus:border-[#0F7D6C]' : 'focus:border-indigo-500'
                }`}
              />
            </div>

            <div className="h-px bg-slate-100 dark:bg-slate-800"></div>

            <h3 className="text-xs font-black uppercase tracking-widest text-slate-400">Filter Specialty</h3>
            <div className="flex flex-wrap lg:flex-col gap-2 max-h-[300px] overflow-y-auto custom-scrollbar pr-2">
              {allSpecialties.map(spec => (
                <button
                  key={spec}
                  onClick={() => setSelectedSpecialty(spec)}
                  className={`px-4 py-3 text-left rounded-xl text-[11px] font-black tracking-wide uppercase transition-all flex items-center justify-between ${
                    selectedSpecialty === spec 
                      ? (tokens.isWarmAcademic ? 'bg-[#0D6E6E] text-white shadow-md' : isClinicalTheme ? 'bg-[#0F7D6C] text-white shadow-md' : 'bg-indigo-600 text-white shadow-md shadow-indigo-600/15')
                      : 'bg-slate-50 dark:bg-slate-950 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-900'
                  }`}
                >
                  <span>{spec}</span>
                  {selectedSpecialty === spec && <ChevronRight size={14} />}
                </button>
              ))}
            </div>
          </div>
          
          <div className={`p-8 rounded-[2.5rem] text-white space-y-4 border ${
            tokens.isWarmAcademic ? 'bg-[#0B5C5C] border-[#0A5252]' : isClinicalTheme ? 'bg-[#0B2942] border-slate-800' : 'bg-gradient-to-br from-indigo-900 to-slate-950 border-white/5'
          }`}>
            <div className={`flex items-center gap-2 ${tokens.isWarmAcademic ? 'text-amber-300' : isClinicalTheme ? 'text-teal-400' : 'text-indigo-400'}`}>
              <ShieldCheck size={20} />
              <span className="text-[10px] font-black tracking-widest uppercase">Verified Curriculum</span>
            </div>
            <h4 className={`text-xl font-black leading-tight ${isClinicalTheme ? 'font-serif' : ''}`}>Guaranteed Compliance Path</h4>
            <p className="text-xs text-slate-200/90 leading-relaxed">
              We coordinate directly with licensing registries and healthcare training boards across multiple state bureaus to verify clinical practice standards.
            </p>
          </div>
        </div>

        {/* Right Profiles Display Grid Column */}
        <div className="lg:col-span-8 space-y-6">
          {isLoading ? (
            <div className="space-y-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-slate-100 dark:bg-slate-900/50 h-56 rounded-[2.5rem] animate-pulse"></div>
              ))}
            </div>
          ) : filteredFaculty.length === 0 ? (
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-12 text-center rounded-[2.5rem]">
              <Users size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="font-extrabold text-slate-500">No matching faculty profiles found.</p>
              <button 
                onClick={() => { setSearchTerm(''); setSelectedSpecialty('All'); }} 
                className="mt-4 px-6 py-2.5 bg-slate-900 text-white text-xs font-bold rounded-xl hover:bg-slate-800 transition-all"
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredFaculty.map((member) => (
                <div 
                  key={member._id} 
                  id={`faculty-member-${member._id}`}
                  className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 md:p-8 rounded-[2.5rem] hover:shadow-xl transition-all duration-300 flex flex-col md:flex-row gap-6 items-start"
                >
                  <div className="relative shrink-0 mx-auto md:mx-0">
                    {member.avatarUrl ? (
                      <img 
                        src={member.avatarUrl} 
                        alt={member.name} 
                        className="w-24 h-24 md:w-28 md:h-28 rounded-3xl object-cover shadow-md border-2 border-indigo-100 dark:border-slate-800"
                        onError={(e) => {
                          // Fallback to initial
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-24 h-24 md:w-28 md:h-28 rounded-3xl bg-indigo-50 dark:bg-slate-850 flex items-center justify-center font-black text-indigo-600 dark:text-indigo-400 text-3xl shadow-inner border border-slate-100 dark:border-slate-800">
                        {member.name.charAt(0)}
                      </div>
                    )}
                    {member.isDesignatedAuthor && (
                      <div className="absolute -bottom-2 -right-2 bg-indigo-600 text-white text-[8px] font-black tracking-widest uppercase px-2.5 py-1 rounded-full shadow-md flex items-center gap-1 border border-indigo-400">
                        <Sparkles size={8} className="fill-white" /> Author
                      </div>
                    )}
                  </div>

                  <div className="flex-1 space-y-3.5 text-center md:text-left min-w-0">
                    <div>
                      <h3 className="text-xl font-black text-slate-900 dark:text-white tracking-tight leading-none">
                        {member.name}
                      </h3>
                      <div className="flex flex-wrap items-center justify-center md:justify-start gap-2 mt-2">
                        <span className="text-xs font-black tracking-wide text-indigo-600 dark:text-indigo-400 uppercase">
                          {member.title}
                        </span>
                        <div className="w-1 h-3 bg-slate-200 dark:bg-slate-800 hidden sm:block"></div>
                        <span className="text-[10px] font-black text-slate-400 tracking-wider uppercase">
                          {member.credentials}
                        </span>
                      </div>
                    </div>

                    <p className="text-xs text-slate-600 dark:text-slate-400 font-medium leading-relaxed">
                      {member.bio}
                    </p>

                    <div className="flex flex-wrap gap-1.5 justify-center md:justify-start pt-1">
                      {Array.isArray(member.specialties) && member.specialties.map(spec => (
                        <span key={spec} className="px-2.5 py-1 bg-slate-50 dark:bg-slate-950 text-slate-500 dark:text-slate-505 border border-slate-150 dark:border-slate-805 rounded-lg text-[9px] font-black uppercase tracking-wider">
                          {spec}
                        </span>
                      ))}
                    </div>

                    {member.email && (
                      <div className="pt-2 border-t border-slate-100 dark:border-slate-805 flex items-center justify-center md:justify-start gap-2 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                        <Mail size={12} />
                        <span>{member.email}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
